import type { DifficultyLevel, Laterality, PhaseKey, PlannedRoutine, PlannedSession, TreatmentPhase, TreatmentTemplateDetails } from "@akina/domain";
import { treatmentUtils } from "@akina/domain";
import type { SharedFormatMessage } from "@akina/infra";
import type { ReactNode } from "react";
import type {
  PersonalizationResponse,
  TreatmentSummaryResponse,
  TreatmentTemplateWithPersonalizationsResponse,
} from "types/schemas";

export const TPM_COPY_PREFIX = "shared.therapyPlanManagement";

/**
 * Unified model for Therapy Plan Management.
 * UI components only consume this type.
 */
export type TherapyPlanManagementData = {
  id: string;
  templateKey: string;
  lastUpdatedAt?: string;
  sessions: PlannedSession[];
  phases: TreatmentPhase[];
  phaseKeyAllocation: string[];
  nextSessionKey?: string;
  currentPhaseKey?: string;
  therapistId?: string;
  patientName?: string;
  stagedPhaseKey?: string | null;
  phaseLogs?: Record<string, { startDate: Date | null; endDate: Date | null }>;
};

/**
 * UI behaviour configuration for any TherapyPlan rendering context.
 */
export type TherapyPlanConfig = {
  isEditable: boolean;
  showDates: boolean;
  showActivation: boolean;
  showCurrentTag: boolean;
  showProgressIndicator?: boolean;
  expandAllPhases?: boolean;
  isCollapsible?: boolean;
  showAssociatedPatients?: boolean;
};

/**
 * Strategy pattern for save/update actions.
 */
export type TPMStrategy = {
  saveSession: (session: PlannedSession) => Promise<void>;
  activatePhase?: (params: { treatmentId: string; targetPhaseKey: string }) => Promise<void>;
  cancelPhaseChange?: (params: { treatmentId: string; targetPhaseKey: string }) => Promise<void>;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
function sortSessionsBySequence<T extends { key: string }>(rawSessions: T[], sessionKeySequence?: string[]): T[] {
  if (sessionKeySequence && sessionKeySequence.length > 0) {
    return sessionKeySequence.map(key => rawSessions.find(s => s.key === key)).filter((s): s is T => s !== undefined);
  }
  return rawSessions;
}

function mapRoutinePlan(r: any) {
  return {
    ...r,
    id: r.id?.toString() ?? r.key,
    routineTemplateKey: r.routineTemplateKey ?? r.key,
    hasLaterality: r.hasLaterality ?? false,
    nSet: r.nSet ?? 1,
  };
}

function mapSessionPlan(s: any, templateKey: string): PlannedSession {
  return {
    ...s,
    id: s.id?.toString() ?? s.key,
    treatmentTemplateKey: s.treatmentTemplateKey ?? templateKey,
    isAssessment: s.isAssessment ?? false,
    isInitialSession: s.isInitialSession ?? false,
    defaultInstancePerWeek: s.defaultInstancePerWeek ?? 1,
    maxInstancePerWeek: s.maxInstancePerWeek ?? 1,
    earliestPossibleWeek: s.earliestPossibleWeek ?? 1,
    latestPossibleWeek: s.latestPossibleWeek ?? 52,
    routinePlanKeySequence: s.routinePlanKeySequence ?? s.routinePlans?.map((r: any) => r.key) ?? [],
    routinePlans: s.routinePlans?.map((r: any) => mapRoutinePlan(r)) ?? [],
  };
}

function getFilteredPhaseKeyAllocation(
  phaseKeyAllocationToSession: string[] | undefined,
  filteredSessionKeySequence: string[],
  rawSessionKeySequence: string[],
): string[] {
  const alignedPhaseKeyAllocation =
    (phaseKeyAllocationToSession ?? []).length === filteredSessionKeySequence.length
      ? (phaseKeyAllocationToSession ?? [])
      : (phaseKeyAllocationToSession ?? []).filter((_phaseKey, idx) => {
          const sessionKey = rawSessionKeySequence[idx];
          return !!sessionKey && !sessionKey.endsWith("00");
        });

  return alignedPhaseKeyAllocation.filter((phaseKey, idx) => phaseKey !== "phase_0" && !!filteredSessionKeySequence[idx]);
}

export function calculatePhaseFirstAppearance(phaseKeyAllocation: string[]): Map<string, number> {
  const phaseFirstAppearance = new Map<string, number>();
  phaseKeyAllocation.forEach((phaseKey, idx) => {
    if (!phaseFirstAppearance.has(phaseKey)) {
      phaseFirstAppearance.set(phaseKey, idx);
    }
  });
  return phaseFirstAppearance;
}

function orderPhasesByAppearance(
  phases: TreatmentTemplateDetails["phases"] | undefined,
  phaseKeyAllocation: string[],
): TreatmentPhase[] {
  const phaseFirstAppearance = calculatePhaseFirstAppearance(phaseKeyAllocation);

  return (phases ?? [])
    .map((p, idx) => ({
      key: p.key,
      name: p.name,
      startWeek: idx * 4 + 1,
      endWeek: (idx + 1) * 4,
      firstAppearanceIndex: phaseFirstAppearance.get(p.key) ?? Infinity,
    }))
    .sort((a, b) => a.firstAppearanceIndex - b.firstAppearanceIndex)
    .map(({ firstAppearanceIndex, ...phase }) => phase);
}

/**
 * Mappers to transform BE schemas into unified TPMData.
 */
export const TPMMappers = {
  fromTreatment: (treatment: any, summary?: TreatmentSummaryResponse | null): TherapyPlanManagementData => {
    const t = treatment;
    const templateKey = t?.key ?? "";
    const rawSessionKeySequence = t?.sessionKeySequence ?? [];
    const rawSessionPlans = t?.sessionPlans ?? [];
    const phasePlans = t?.phases?.phasePlans ?? [];

    const filteredSessionKeySequence = rawSessionKeySequence.filter((key: string) => !key.endsWith("00"));
    const filteredSessionPlans = rawSessionPlans.filter((s: any) => s?.key && !s.key.endsWith("00"));
    const sessionPlans = sortSessionsBySequence(filteredSessionPlans, filteredSessionKeySequence);
    const phaseLogs = t?.phases?.phaseLogs;

    const filteredPhaseKeyAllocation = getFilteredPhaseKeyAllocation(
      t?.phaseKeyAllocationToSession,
      filteredSessionKeySequence,
      rawSessionKeySequence,
    );

    return {
      id: t?.treatmentId ?? "",
      templateKey,
      sessions: sessionPlans.map((s: any) => mapSessionPlan(s, templateKey)),
      phases: phasePlans.map((p: any) => {
        const phaseLog = phaseLogs?.[p.key];
        const startDate = phaseLog?.startDate ?? phaseLog?.start_date;
        const endDate = phaseLog?.endDate ?? phaseLog?.end_date;
        
        const parseDate = (dateStr: string | null | undefined): Date | undefined => {
          if (!dateStr) return undefined;
          const parsed = new Date(dateStr);
          return Number.isNaN(parsed.getTime()) ? undefined : parsed;
        };

        return {
          key: p.key,
          name: p.name ?? p.key,
          startWeek: p.startWeek,
          endWeek: p.endWeek,
          actualStartDate: parseDate(startDate),
          actualEndDate: parseDate(endDate),
          scheduledStartDate: parseDate(p.scheduledStartDate),
        };
      }),
      phaseKeyAllocation: filteredPhaseKeyAllocation,
      nextSessionKey: t?.nextSessionPlanKey || undefined,
      currentPhaseKey: t?.phases?.currentPhaseKey || summary?.phases?.currentPhaseKey || undefined,
      therapistId: summary?.therapistId || undefined,
      patientName: summary ? `${summary.firstName ?? ""} ${summary.lastName ?? ""}`.trim() : undefined,
      stagedPhaseKey: summary?.stagedPhaseKey || undefined,
      phaseLogs: phaseLogs,
      lastUpdatedAt: t?.lastUpdate || t?.lastUpdatedAt || undefined,
    };
  },

  fromPersonalization: (
    personalization: PersonalizationResponse | undefined,
    templateDetails: TreatmentTemplateDetails | undefined,
    phaseKeyAllocationToSession?: string[],
    sessionKeySequence?: string[],
  ): TherapyPlanManagementData => {
    const realPhases = templateDetails?.phases?.filter(p => treatmentUtils.getPhaseKeyIndex(p.key) > 0) ?? [];
    const firstRealPhaseKey = realPhases[0]?.key ?? templateDetails?.phases?.[0]?.key ?? "";
    const templateKey = personalization?.treatmentTemplateKey ?? templateDetails?.key ?? "";

    const rawSessionKeySequence = sessionKeySequence ?? [];
    const filteredSessionKeySequence = rawSessionKeySequence.filter(key => !key.endsWith("00"));
    const rawSessions = (personalization?.sessionPlans ?? []).filter((s: any) => s?.key && !s.key.endsWith("00"));
    const sessions = sortSessionsBySequence(rawSessions, filteredSessionKeySequence);
    const filteredPhaseKeyAllocation = getFilteredPhaseKeyAllocation(
      phaseKeyAllocationToSession,
      filteredSessionKeySequence,
      rawSessionKeySequence,
    );
    const phasesOrdered = orderPhasesByAppearance(templateDetails?.phases, filteredPhaseKeyAllocation);

    return {
      id: personalization?.id?.toString() ?? "template-view",
      templateKey,
      sessions: sessions.map((s: any) => mapSessionPlan(s, templateKey)),
      phases: phasesOrdered,
      phaseKeyAllocation: filteredPhaseKeyAllocation,
      nextSessionKey: sessions[0]?.key || undefined,
      currentPhaseKey: firstRealPhaseKey || undefined,
    };
  },

  fromTemplate: (
    templateResponse: TreatmentTemplateWithPersonalizationsResponse["treatmentTemplate"] | undefined,
    templateDetails: TreatmentTemplateDetails | undefined,
  ): TherapyPlanManagementData => {
    const realPhases = templateDetails?.phases?.filter(p => treatmentUtils.getPhaseKeyIndex(p.key) > 0) ?? [];
    const firstRealPhaseKey = realPhases[0]?.key ?? templateDetails?.phases?.[0]?.key ?? "";
    const templateKey = templateResponse?.treatmentTemplateKey ?? templateDetails?.key ?? "";
    const t = templateResponse;
    const rawSessionKeySequence = t?.sessionKeySequence ?? [];
    const filteredSessionKeySequence = rawSessionKeySequence.filter(key => !key.endsWith("00"));
    const filteredPhaseKeyAllocation = getFilteredPhaseKeyAllocation(
      t?.phaseKeyAllocationToSession,
      filteredSessionKeySequence,
      rawSessionKeySequence,
    );
    const sessionTemplates = t?.sessionTemplates ?? [];

    const sessionTemplateMap = new Map(sessionTemplates.map(st => [st.sessionTemplateKey, st]));

    const sessions: PlannedSession[] = filteredSessionKeySequence.map((sessionKey, idx) => {
      const sessionTemplate = sessionTemplateMap.get(sessionKey);
      const routineKeySequence = sessionTemplate?.routineKeySequence ?? [];
      const routineTemplates = sessionTemplate?.routineTemplates ?? [];

      const routineTemplateMap = new Map(routineTemplates.map(rt => [rt.routineTemplateKey, rt]));

      const routinePlans = routineKeySequence.map((routineKey, routineIdx) => {
        const routineTemplate = routineTemplateMap.get(routineKey);
        return {
          id: routineIdx + 1,
          key: routineKey,
          treatmentId: "",
          treatmentTemplateKey: templateKey,
          routineTemplateKey: routineTemplate?.routineTemplateKey ?? routineKey,
          exerciseTemplateKey: routineTemplate?.exerciseTemplateKey ?? "",
          nSet: routineTemplate?.nSet ?? 1,
          nRepetitions: routineTemplate?.nRepetitions ?? null,
          duration: routineTemplate?.duration ?? null,
          breakDuration: routineTemplate?.breakDuration ?? null,
          difficultyLevel: (routineTemplate?.difficultyLevel ?? "easy") as "easy" | "medium" | "hard",
          laterality: routineTemplate?.laterality ?? "both",
          hasLaterality: routineTemplate?.laterality !== "both",
          order: routineIdx,
        };
      });

      return {
        id: sessionKey,
        key: sessionKey,
        treatmentId: "",
        treatmentTemplateKey: templateKey,
        sessionTemplateKey: sessionKey,
        isAssessment: false,
        isInitialSession: idx === 0,
        defaultInstancePerWeek: 1,
        maxInstancePerWeek: 1,
        earliestPossibleWeek: 1,
        latestPossibleWeek: 52,
        routinePlanKeySequence: routineKeySequence,
        routinePlans,
      } as PlannedSession;
    });

    const phasesOrdered = orderPhasesByAppearance(templateDetails?.phases, filteredPhaseKeyAllocation);

    return {
      id: "template-view",
      templateKey,
      sessions,
      phases: phasesOrdered,
      phaseKeyAllocation: filteredPhaseKeyAllocation,
      nextSessionKey: sessions[0]?.key || undefined,
      currentPhaseKey: firstRealPhaseKey || undefined,
    };
  },
};

type GetTherapyPlanManagementDataParams = {
  personalization: PersonalizationResponse | null | undefined;
  personalizationId: string | undefined;
  treatmentTemplate: TreatmentTemplateWithPersonalizationsResponse["treatmentTemplate"] | undefined;
  templateDetails: TreatmentTemplateDetails | undefined;
};

export function getTherapyPlanManagementData(params: GetTherapyPlanManagementDataParams): TherapyPlanManagementData | null {
  const { personalization, personalizationId, treatmentTemplate, templateDetails } = params;
  if (!templateDetails) return null;

  if (personalization) {
    const phaseKeyAllocationToSession =
      personalization.phaseKeyAllocationToSession ?? treatmentTemplate?.phaseKeyAllocationToSession;
    const sessionKeySequence = personalization.sessionKeySequence ?? treatmentTemplate?.sessionKeySequence;
    return TPMMappers.fromPersonalization(
      personalization,
      templateDetails,
      phaseKeyAllocationToSession,
      sessionKeySequence,
    );
  }
  if (!personalizationId && treatmentTemplate) {
    return TPMMappers.fromTemplate(treatmentTemplate, templateDetails);
  }
  return TPMMappers.fromPersonalization(
    undefined,
    templateDetails,
    treatmentTemplate?.phaseKeyAllocationToSession,
    treatmentTemplate?.sessionKeySequence,
  );
}

/**
 * Predefined TherapyPlan rendering configs, one per use-case.
 */
export const therapyPlanConfigs = {
  personalizationEditor: {
    isEditable: true,
    showDates: false,
    showActivation: false,
    showCurrentTag: false,
    showProgressIndicator: false,
    expandAllPhases: true,
    isCollapsible: false,
    showAssociatedPatients: false,
  } satisfies TherapyPlanConfig,

  therapyPlanVisualizer: {
    isEditable: false,
    showDates: true,
    showActivation: false,
    showCurrentTag: false,
    showProgressIndicator: false,
    expandAllPhases: true,
    isCollapsible: false,
  } satisfies TherapyPlanConfig,

  templateVisualizer: {
    isEditable: false,
    showDates: false,
    showActivation: false,
    showCurrentTag: false,
    showProgressIndicator: false,
    expandAllPhases: true,
    isCollapsible: false,
    showAssociatedPatients: false,
  } satisfies TherapyPlanConfig,

  dashboardVisualizer: {
    isEditable: false,
    showDates: true,
    showActivation: false,
    showCurrentTag: true,
    showProgressIndicator: true,
    expandAllPhases: false,
    isCollapsible: true,
  } satisfies TherapyPlanConfig,

  treatmentEditor: {
    isEditable: true,
    showDates: true,
    showActivation: true,
    showCurrentTag: true,
    showProgressIndicator: false,
    expandAllPhases: false,
    isCollapsible: true,
    showAssociatedPatients: true,
  } satisfies TherapyPlanConfig,
};

export type SessionEditWarnings = "sessionTooLong" | "sessionTooShort";

export type TherapyPlanManagementState = {
  isEditing: boolean;
  isSaving: boolean;
  sessionKey?: string;
  editWarning?: SessionEditWarnings;
  modalOpen: "discardChanges" | "editRoutine" | "addRoutine" | "confirmPhaseChange" | null;
  discardChangesParams: { onConfirm?: () => void };
  confirmPhaseChangeParams: { targetPhaseKey?: string; phaseNumber?: number; patientName?: string };
  editRoutineParams?: {
    routine: PlannedRoutine | null;
    exerciseName?: string | null;
    selectedRoutines?: PlannedRoutine[];
  };
  addRoutineParams: {
    treatmentId?: string;
    exerciseTemplateKey?: string | null;
    exerciseName?: string | null;
    selectedRoutines: PlannedRoutine[];
  };

  actions: {
    cleanup: () => void;

    setIsEditing: (isEditing: boolean) => void;
    setSessionKey: (key?: string) => void;
    setModalOpen: (type: TherapyPlanManagementState["modalOpen"]) => void;
    setDiscardChangesParams: (params: TherapyPlanManagementState["discardChangesParams"]) => void;
    setConfirmPhaseChangeParams: (params: TherapyPlanManagementState["confirmPhaseChangeParams"]) => void;
    setEditRoutineParams: (params: TherapyPlanManagementState["editRoutineParams"]) => void;
    setAddRoutineParams: (params: Partial<TherapyPlanManagementState["addRoutineParams"]>) => void;
    closeModal: () => void;

    startUpdate: (session: PlannedSession, phaseKey: PhaseKey) => void;
    cancelUpdate: () => void;
    saveUpdate: (onSuccess?: () => void) => Promise<void>;

    updatePlannedSession: (session: PlannedSession | null) => void;
    updatePlannedRoutine: (routine: PlannedRoutine | null, oldKey: string) => void;
    addPlannedRoutine: (routine: PlannedRoutine) => void;
    updateEditWarnings: (session: PlannedSession | null) => void;
  };
};


export type RoutineInformationProps = {
  exerciseName: string;
  difficulty: DifficultyLevel | null;
  actions?: ReactNode;
  routineSetAndBreaksInfo: {
      nSet: number;
      nRepetitions?: number;
      duration?: string;
      breakDuration: string;
      isRepBased?: boolean;
  };
  routineChosenLaterality?: Laterality | null;
  className?: string;
  showLateralityInDetails?: boolean;
  formatMessage: SharedFormatMessage;
};